/**
 * Utility functions for managing UI widget state persistence via sessionStorage
 * This enables accordion states, pagination, and other UI elements to persist 
 * when users navigate back to the page during their session
 */

const UI_STATE_KEY = 'telescopeUIState';

/**
 * Gets the complete UI state from sessionStorage
 * @returns {Object} - Complete UI state object
 */
export function getUIState() {
  try {
    const state = sessionStorage.getItem(UI_STATE_KEY);
    return state ? JSON.parse(state) : {};
  } catch (error) {
    console.warn('Failed to parse UI state from sessionStorage:', error);
    return {};
  }
}

/**
 * Saves the complete UI state to sessionStorage
 * @param {Object} state - Complete UI state object
 */
export function saveUIState(state) {
  try {
    sessionStorage.setItem(UI_STATE_KEY, JSON.stringify(state));
  } catch (error) {
    console.warn('Failed to save UI state to sessionStorage:', error);
  }
}

/**
 * Gets UI state for a specific page
 * @param {string} pageKey - Unique identifier for the page (e.g., 'supplierSearch')
 * @returns {Object} - Page-specific UI state
 */
export function getPageUIState(pageKey) {
  const fullState = getUIState();
  return fullState[pageKey] || {};
}

/**
 * Saves UI state for a specific page
 * @param {string} pageKey - Unique identifier for the page
 * @param {Object} pageState - Page-specific UI state
 */
export function savePageUIState(pageKey, pageState) {
  const fullState = getUIState();
  fullState[pageKey] = pageState;
  saveUIState(fullState);
}

/**
 * Updates a specific UI element state within a page
 * @param {string} pageKey - Page identifier
 * @param {string} elementKey - UI element identifier
 * @param {any} value - Value to save
 */
export function updateElementState(pageKey, elementKey, value) {
  const pageState = getPageUIState(pageKey);
  pageState[elementKey] = value;
  savePageUIState(pageKey, pageState);
}

/**
 * Gets state for a specific UI element
 * @param {string} pageKey - Page identifier
 * @param {string} elementKey - UI element identifier
 * @param {any} defaultValue - Default value if not found
 * @returns {any} - Element state value
 */
export function getElementState(pageKey, elementKey, defaultValue = null) {
  const pageState = getPageUIState(pageKey);
  return pageState[elementKey] !== undefined ? pageState[elementKey] : defaultValue;
}

/**
 * Clears all UI state (useful for logout or session reset)
 */
export function clearUIState() {
  try {
    sessionStorage.removeItem(UI_STATE_KEY);
  } catch (error) {
    console.warn('Failed to clear UI state from sessionStorage:', error);
  }
}

/**
 * Clears UI state for a specific page
 * @param {string} pageKey - Page identifier to clear
 */
export function clearPageUIState(pageKey) {
  const fullState = getUIState();
  delete fullState[pageKey];
  saveUIState(fullState);
}

// Pre-defined page keys for consistency
export const PAGE_KEYS = {
  SUPPLIER_SEARCH: 'supplierSearch',
  OPPORTUNITY_SEARCH: 'opportunitySearch',
  PROJECT_LIST: 'projectList',
  DASHBOARD: 'dashboard',
  PRODUCT_INPUT_DETAIL: 'productInputDetail',
  SAVED_SUPPLIER_SEARCHES: 'savedSupplierSearches',
  SOURCING_PROJECTS: 'sourcingProjects',
  MY_SUPPLIERS: 'mySuppliers'
};

// Pre-defined element keys for common UI components
export const ELEMENT_KEYS = {
  // Pagination
  PAGE_SIZE: 'pageSize',
  CURRENT_PAGE: 'currentPage',
  
  // View preferences
  SHOW_INFO_PANEL: 'showInfoPanel',
  LIST_VIEW_MODE: 'listViewMode',
  
  // Search panel states
  SHOW_ADVANCED_SEARCH: 'showAdvancedSearch',
  SEARCH_PANEL_COLLAPSED: 'searchPanelCollapsed',

  // Saved supplier searches page
  ACTIVE_ASSOCIATION_TAB: 'activeAssociationTab',
  MY_SEARCHES_ONLY: 'mySearchesOnly',
  SEARCH_TEXT: 'searchText',

  // Sourcing projects page
  NAME_FILTER: 'nameFilter',
  STATUS_TAB: 'statusTab',
  MY_PROJECTS_ONLY: 'myProjectsOnly',
  SORT_FIELD: 'sortField',
  SORT_DIRECTION: 'sortDirection'
};